import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { useAuth } from "@frontegg/react";
import { UPDATE_PASSWORD } from "../../../graphql/user";
import PasswordRequirements from "./PasswordRequirements";
import passwordRequirements from "../../../validations/passwordRequirements";
import { Button } from "@mui/material";
import { Drawer, toast } from "@outpave/ds";

interface PasswordDrawerProps {
  open: boolean;
  handleCloseDrawer: () => void;
}

export interface FormikPasswordRequirement {
  password: string;
  newPassword: string;
  reTypePassword: string;
}

const PasswordDrawer: React.FC<PasswordDrawerProps> = (props) => {
  const { user } = useAuth();

  const [isSavingPassword, setIsSavingPassword] = useState<boolean>(false);

  const { open, handleCloseDrawer } = props;

  const [updatePassword] = useMutation(UPDATE_PASSWORD, {
    onCompleted: () => {
      formikPasswordRequirements.resetForm();
      setIsSavingPassword(false);
      toast.success(`${t("User password updated!")}`);
      handleCloseDrawer();
    },
    onError: (error) => {
      setIsSavingPassword(false);
      toast.error(error.message);
    },
  });

  const formikPasswordRequirements = useFormik<FormikPasswordRequirement>({
    initialValues: {
      password: "",
      newPassword: "",
      reTypePassword: "",
    },
    validationSchema: passwordRequirements,
    onSubmit: (values) => {
      setIsSavingPassword(true);
      updatePassword({
        variables: {
          id: user?.id,
          input: {
            password: values.password,
            newPassword: values.newPassword,
          },
        },
      });
    },
  });

  const { t } = useTranslation();

  return (
    <Drawer
      open={open}
      onClose={() => {
        if (isSavingPassword) return;
        handleCloseDrawer();
      }}
      title={[t("Profile"), t("Your Password")]}
      loading={isSavingPassword}
      footer={
        <>
          <Button
            variant="default-outlined"
            disabled={isSavingPassword}
            onClick={handleCloseDrawer}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="primary-filled"
            disabled={isSavingPassword}
            onClick={() => {
              formikPasswordRequirements.handleSubmit();
            }}
          >
            {t("Save")}
          </Button>
        </>
      }
    >
      <PasswordRequirements formik={formikPasswordRequirements} />
    </Drawer>
  );
};

export default PasswordDrawer;
